import { Link } from 'react-router-dom';
import { linhaData } from '../utils';
import type { EstadoCriacao } from '../types';

interface Props {
  estado: EstadoCriacao;
  aoAbrirDetalhes: () => void;
}

// Cartão fixo no topo da conversa: mostra o rascunho que está sendo criado,
// o que ainda falta para publicar e os avisos. Tocar no corpo abre os detalhes.
export default function CartaoEventoCriacao({ estado, aoAbrirDetalhes }: Props) {
  const ev = estado.evento;
  const local = [ev.cidade, ev.uf].filter(Boolean).join('/');
  const referencia = [linhaData(ev.inicio_em), local].filter(Boolean).join(' · ');

  const falta = estado.falta_para_publicar || [];
  const pronto = falta.length === 0;
  // Evento já publicado nunca aparece como "Rascunho" no cartão.
  const rascunho = !ev.status || ev.status === 'rascunho';

  const avisos = (estado.avisos || [])
    .map((a) => (typeof a === 'string' ? a : a?.texto))
    .filter((a): a is string => !!a);

  return (
    <div className="overflow-hidden rounded-[18px] bg-background-50 shadow-pcard">
      <button
        type="button"
        onClick={aoAbrirDetalhes}
        className="aperta flex w-full cursor-pointer items-center gap-3 p-3 text-left"
      >
        <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-background-100 text-foreground-400">
          {ev.capa_url ? (
            <>
              <img
                src={ev.capa_url}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-60 blur-md"
                decoding="async"
              />
              <img
                src={ev.capa_url}
                alt=""
                className="relative h-full w-full object-contain object-center"
                decoding="async"
              />
            </>
          ) : (
            <i className="ri-image-line text-xl" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[15px] font-extrabold text-foreground-950">
              {ev.nome || 'Novo evento'}
            </span>
            {rascunho ? (
              <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-700">
                Rascunho
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-success-100 px-2 py-0.5 text-[11px] font-semibold text-success-800">
                Publicado
              </span>
            )}
          </span>
          {referencia && (
            <span className="mt-0.5 block truncate text-[12px] text-foreground-600">
              {referencia}
            </span>
          )}
        </span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-foreground-400">
          <i className="ri-arrow-down-s-line text-xl" aria-hidden />
        </span>
      </button>

      <div className="border-t border-background-200 px-3 py-2.5">
        {pronto ? (
          <div className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold text-success-600">
              <i className="ri-checkbox-circle-line shrink-0" aria-hidden />
              <span className="truncate">Pronto para publicar</span>
            </span>
            <Link
              to={estado.links.painel}
              className="aperta flex h-9 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full bg-primary-500 px-4 text-[13px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              Revisar e publicar
            </Link>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <p className="min-w-0 flex-1 text-[13px] leading-snug text-foreground-700">
              <span className="font-semibold text-foreground-800">Falta: </span>
              {falta.join(' · ')}
            </p>
            <Link
              to={estado.links.painel}
              className="aperta flex h-9 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
            >
              Ver no painel
            </Link>
          </div>
        )}

        {avisos.map((texto) => (
          <div key={texto} className="mt-2 flex items-start gap-1.5 text-[12px] text-warning-700">
            <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">{texto}</span>
            <Link
              to={estado.links.editar}
              className="shrink-0 whitespace-nowrap font-semibold text-warning-700 underline"
            >
              Resolver
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}